// utils/coordinateUtils.js
// Pure utility functions for coordinate conversion and formatting
// Used by CameraModal overlay and PhotoMetadataOverlay

import { forward as mgrsForward } from "mgrs";

/**
 * Convert lat/lng to MGRS (Military Grid Reference System) string.
 * @param {number} lat - Latitude in decimal degrees
 * @param {number} lng - Longitude in decimal degrees
 * @returns {string|null} MGRS string (e.g., "10TGR2523440128") or null on error
 */
export const toMGRS = (lat, lng) => {
  try {
    return mgrsForward([lng, lat], 5);
  } catch {
    return null;
  }
};

/**
 * Format elevation with direction arrow.
 * @param {number|null} altitude - Altitude in meters
 * @returns {string} Formatted string (e.g., "\u2191 47.2m") or empty
 */
export const formatElevation = (altitude) => {
  if (altitude == null) return "";
  const arrow = altitude >= 0 ? "\u2191" : "\u2193";
  return `${arrow} ${Math.abs(altitude).toFixed(1)}m`;
};

/**
 * Format GPS accuracy as +/- meters.
 * @param {number|null} accuracy - Accuracy in meters
 * @returns {string} Formatted string (e.g., "\u00B13.1m") or empty
 */
export const formatAccuracy = (accuracy) => {
  if (accuracy == null) return "";
  return `\u00B1${accuracy.toFixed(1)}m`;
};

/**
 * Format compass bearing with cardinal direction and "Facing" label.
 * @param {number|null} bearing - Bearing in degrees (0-360)
 * @returns {string} Formatted string (e.g., "Facing: NW 315\u00B0") or empty
 */
export const formatBearing = (bearing) => {
  if (bearing == null) return "";
  const dirs = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  return `Facing: ${dirs[Math.round(bearing / 45) % 8]} ${Math.round(bearing)}\u00B0`;
};

/**
 * Format GPS coordinates as decimal degrees.
 * @param {number|null} lat - Latitude
 * @param {number|null} lng - Longitude
 * @returns {string} Formatted string (e.g., "45.5231, -122.6765") or empty
 */
export const formatCoordinates = (lat, lng) => {
  if (lat == null || lng == null) return "";
  return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
};

/**
 * Format captured time for display. Prefers pre-formatted local string,
 * falls back to converting the UTC ISO string to local.
 * @param {string|null} capturedAtLocal - Pre-formatted local time string
 * @param {string|Date|null} capturedAt - UTC ISO string or Date object (fallback)
 * @returns {string} Formatted local datetime (e.g., "02/19/2026 2:32 PM") or empty
 */
export const formatCapturedAt = (capturedAtLocal, capturedAt) => {
  if (capturedAtLocal) return capturedAtLocal;
  if (!capturedAt) return "";
  try {
    const date = capturedAt instanceof Date ? capturedAt : new Date(capturedAt);
    return date.toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return "";
  }
};

/**
 * Format captured_at UTC ISO string to a short UTC time display.
 * @param {string|Date|null} capturedAt - UTC ISO string or Date object
 * @returns {string} Formatted UTC time (e.g., "19:32 UTC") or empty
 */
export const formatCapturedAtUTC = (capturedAt) => {
  if (!capturedAt) return "";
  try {
    const date = capturedAt instanceof Date ? capturedAt : new Date(capturedAt);
    const hours = date.getUTCHours().toString().padStart(2, "0");
    const minutes = date.getUTCMinutes().toString().padStart(2, "0");
    return `${hours}:${minutes} UTC`;
  } catch {
    return "";
  }
};
